import dynamic from 'next/dynamic';
import { useState, useEffect, memo } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Typography, Box, FormControl, OutlinedInput, Button, FormHelperText } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { useTranslation } from 'next-i18next';

import { DiscountBox, FormBox, ImageBox } from './style';

import { DialogType } from '.';

import couponAPI from 'src/api/coupon';
import { NextImage } from 'src/components/atoms/image';
import { useAppData, useProfile } from 'src/helpers/use';
import { getAssetUrl, numberWithCommas, validateEmail } from 'src/helpers/utils';

const ReCAPTCHA = dynamic(import('src/components/atoms/recaptcha'));

type Props = {
  dialog: DialogType;
  setOpenAlert: () => void;
  setAlert: any;
};

const CouponModal = ({ dialog, setOpenAlert, setAlert }: Props) => {
  const { appData } = useAppData();
  const { mutate } = useProfile();
  const { t } = useTranslation('common');
  const [email, setEmail] = useState<string>('');
  const [recaptcha, setRecaptcha] = useState<string>('');
  const [error, setError] = useState({ email: false, recaptcha: false, messageEmail: '' });
  const [recaptchaRef, setRecaptchaRef] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isRecaptchaNeeded, setIsRecaptchaNeeded] = useState<boolean>(false);

  const onChange = (value: string | null) => {
    setRecaptcha(value!);
    if (error.recaptcha) {
      setError({ ...error, recaptcha: false });
    }
  };

  const onExpired = () => {
    setRecaptcha('');
  };

  const handleSubmit = async () => {
    if (!email) {
      error.email = true;
      error.messageEmail = 'please_enter_email';
    }
    if (email && !validateEmail(email)) {
      error.email = true;
      error.messageEmail = 'please_enter_valid_email';
    }
    if (!recaptcha) {
      error.recaptcha = true;
    }
    if (error.email || error.recaptcha) {
      return setError({ ...error });
    }
    setLoading(true);
    const res: any = await couponAPI.createNewUser({ email, recaptchaResponse: recaptcha });
    if (res.status === 201) {
      await mutate();
      setAlert({ message: 'coupon.received_discount_successfully' });
    } else {
      setAlert({ message: res.message, isError: true });
    }
    setLoading(false);
    dialog.close();
    clear();
    setOpenAlert();
  };

  const handleEmail = ({ target }: any) => {
    setEmail(target.value);
    if (error.email) {
      setError({ ...error, email: false });
    }
  };

  const clear = () => {
    setError({ email: false, recaptcha: false, messageEmail: '' });
    setEmail('');
    setRecaptcha('');
    recaptchaRef?.current?.reset();
  };

  useEffect(() => {
    if (dialog.opened) {
      setIsRecaptchaNeeded(true);
    }

    return () => {
      clear();
    };
  }, [dialog.opened]);

  return (
    <Box sx={{ m: '-16px', width: { md: '500px' } }}>
      <ImageBox>
        <Box sx={{ position: 'relative', width: '100.1%', pb: '42.5%' }}>
          <NextImage src={getAssetUrl('discount-coupon.png')} alt="coupon" layout="fill" />
        </Box>
        <DiscountBox>
          <Box className="title">{t('coupon.offer_discount')}</Box>
          <Box className="price">{numberWithCommas(appData?.couponNewUser.price!)} บาท</Box>
          <Box className="desc">{t('coupon.for_first_course')}</Box>
          <Box className="remark">{t('coupon.remark')}</Box>
        </DiscountBox>
      </ImageBox>

      <Box sx={{ position: 'absolute', top: { xs: -2, md: 0 }, right: { xs: -2, md: 0 } }}>
        <IconButton
          onClick={() => {
            dialog.close();
            clear();
          }}
        >
          <CloseIcon sx={{ fill: '#BDBDBD', width: { xs: 18, md: 20 }, height: { xs: 18, md: 20 } }} />
        </IconButton>
      </Box>

      <FormBox
        sx={{
          mt: 2,
          textAlign: 'center',
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography
          sx={{
            width: { xs: '258px', md: 'auto' },
            whiteSpace: { xs: 'pre-line', md: 'normal' },
            wordSpacing: { xs: '200px', md: 'normal' },
            lineHeight: '24px',
          }}
          color="primary.main"
          variant="h4"
        >
          {t('coupon.get_discount_coupon_for_first_course')}
        </Typography>
        <Typography
          variant="body2_deprecated"
          sx={{
            lineHeight: '22px',
            marginTop: '4px',
            fontFamily: 'Lanna',
            width: {
              xs: '258px',
              md: 'auto',
            },
          }}
        >
          {t('coupon.please_enter_email_and_verify_recaptcha')}
        </Typography>

        <FormControl className="form" sx={{ m: 1, mt: 2 }} variant="outlined" size="small">
          <OutlinedInput placeholder={t('email')} error={error.email} value={email} onChange={handleEmail} />
          {error.email && <FormHelperText sx={{ color: 'red', m: '0 0.5rem' }}>{t(error.messageEmail)}</FormHelperText>}
          <Box sx={{ mt: 2 }}>
            {isRecaptchaNeeded && (
              <ReCAPTCHA onChange={onChange} onExpired={onExpired} setRecaptchaRef={setRecaptchaRef} />
            )}
          </Box>
          {error.recaptcha && (
            <FormHelperText sx={{ color: 'red', m: '0 0.5rem' }}>{t('please_verify')}</FormHelperText>
          )}
          <Button
            disabled={loading}
            variant="contained"
            color="primary"
            sx={{ my: 3 }}
            className="button-submit-coupon"
            onClick={handleSubmit}
          >
            {t('claim_it')}!
          </Button>
        </FormControl>
      </FormBox>
    </Box>
  );
};

export default memo(CouponModal);
